import { createSlice, createSelector } from '@reduxjs/toolkit';
import { sendChatMessageThunk } from '../thunks/sendChatMessage';
import { fetchChatMessagesThunk } from '../thunks/fetchChatMessages';
import { getImageUrlsThunk } from '../thunks/getImageUrls';
import { handleWebsocketMessageThunk } from '../thunks/handleWebsocketMessage';
import { getProjectThreadThunk } from '../thunks/getProjectThread';
import { getProjectThreadsThunk } from '../thunks/getProjectThreads';
import { loadThreadOnDemand } from '../thunks/loadThreadOnDemand';
import { getLastReadMessagesForThreadThunk } from '../thunks/getLastReadMessagesForThread';
import { setReadBy } from '../utils/setReadBy';
import { getMessageReadTimeThunk } from '../thunks/getMessageReadTime';
import { getPrivateThreadThunk } from '../thunks/getPrivateThread';
import { getPrivateThreadsThunk } from '../thunks/getPrivateThreads';
import { getThreadVideoSessionsThunk } from '../thunks/getThreadVideoSessions';

// Returns the more recent of two ISO timestamp strings; null if both absent
const maxTimestamp = (a, b) => (!a ? b || null : !b ? a : a >= b ? a : b);

// Keep per-thread message caches for the most recently viewed threads (LRU) so
// returning to a thread renders instantly, without letting a long session
// accumulate every thread's history in memory.
const MESSAGE_CACHE_LIMIT = 20;

export const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    allMessagesFetched: false,
    allUsers: {},
    isChatDialogOpen: false,
    isChatMinimized: false,
    lastEvaluatedKey: null,
    lastReadMessage: null,
    lastReadMessages: [],
    loading: true,
    messages: [],
    // Per-thread stale-while-revalidate cache: { [threadId]: { messages,
    // lastEvaluatedKey, allMessagesFetched } }, plus an LRU order list.
    messagesCache: {},
    messagesCacheOrder: [],
    needImageUrls: false,
    newVideoChatSessionStarted: false,
    notifyMessage: null,
    openedAttachment: null,
    project: null,
    projectUsers: {},
    thread: null,
    threads: {},
    threadsByProject: {},
    unreadChatMessagesCount: 0,
    users: {},
    videoChatOpen: false,
    videoChatSession: null,
    videoSessions: [],
    // Per-thread cache of loaded video-call history, mirroring messagesCache and
    // sharing its LRU order (messagesCacheOrder).
    videoSessionsCache: {},
    videoSessionsLoading: false,
    videoSessionsLastEvaluatedKey: null,
    chatWebsocket: null,
    subThreads: [],
    // Meetings scheduled on a thread, keyed by threadId. Seeded by the dialog's
    // fetch and the app-load "upcoming meetings" fetch, then kept live by MEETING
    // websocket events. Drives the dialog banners/markers and the reminder
    // scheduler. Not persisted — rebuilt on each load.
    meetingsByThread: {},
    // Ongoing (non-private) video calls, keyed by threadId — the id websocket
    // frames carry. Seeded from the project-threads fetch and kept live by
    // VIDEOCHAT_SESSION events for every thread the user is subscribed to, so
    // it stays correct for projects whose chat isn't open. Kept separate from
    // threadsByProject, which is replaced wholesale by its own thunks.
    activeCallsByThreadId: {},
  },
  reducers: {
    addMessage: (state, action) => {
      const message = action.payload;
      if (message.isAttachment) {
        const attachedTo = state.messages.find(
          (m) => m.id === message.attachedTo
        );
        if (attachedTo) {
          // Carry the blob preview URL into the attachment object so it can
          // be used as a fallback until the real signed URL is ready.
          if (attachedTo.imagePreview) {
            message.imagePreview = attachedTo.imagePreview;
          }
          attachedTo.attachments = [message];
          delete attachedTo.imagePreview;
          delete attachedTo.attachmentPreviewName;
          state.needImageUrls = true;
        }
      } else {
        // Check if message already exists to avoid duplicates
        const messageExists =
          message.id && state.messages.some((m) => m.id === message.id);
        if (!messageExists) {
          state.messages = [message, ...state.messages];
          if (state.isChatDialogOpen) {
            state.lastReadMessage = message;
          }
        }
      }
    },
    addMessagesToStart: async (state, action) => {
      const { messages } = action.payload;
      const nonAttachmentMessages = messages
        .filter((message) => !message.isAttachment)
        .map((message) => setReadBy(message, state));
      const attachments = messages.filter(
        (message) => message.isAttachment && message.attachedTo
      );
      const newMessages = [...state.messages, ...nonAttachmentMessages];
      for (const attachment of attachments) {
        const attachedTo = newMessages.find(
          (message) => message.id === attachment.attachedTo
        );
        if (attachedTo) {
          attachedTo.attachments = [attachment];
          delete attachedTo.imagePreview;
          delete attachedTo.attachmentPreviewName;
        }
      }

      state.messages = newMessages;
    },
    addUnreadMessageToThread: (state, action) => {
      const { projectId, originalThreadId, childThread, threadId } = action;

      const threadByProject = state.threadsByProject[projectId];
      const privateThread = state.threads[threadId];

      if (!threadByProject && !privateThread) {
        // Thread doesn't exist in state - this shouldn't happen anymore
        // but we return gracefully to avoid errors
        return;
      }
      if (childThread) {
        if (threadByProject) {
          threadByProject.unreadMessagesInChildProjects++;
          const childThreadInProject = threadByProject.children.find(
            (child) => child.id === originalThreadId
          );
          if (childThreadInProject) {
            childThreadInProject.unreadMessagesCount++;
            childThreadInProject.hasMessages = true;
            childThreadInProject.lastMessageAt = new Date().toISOString();
          }
        }
      } else {
        if (threadByProject) {
          threadByProject.unreadMessagesCount++;
          threadByProject.hasMessages = true;
          threadByProject.lastMessageAt = new Date().toISOString();
        }
        if (privateThread) {
          privateThread.unreadMessagesCount++;
          privateThread.hasMessages = true;
          privateThread.lastMessageAt = new Date().toISOString();
        }
      }

      const parentProjectId = threadByProject?.parent;
      if (parentProjectId) {
        try {
          chatSlice.caseReducers.addUnreadMessageToThread(state, {
            projectId: parentProjectId,
            originalThreadId:
              originalThreadId ||
              threadByProject.threadId ||
              threadByProject.id,
            childThread: threadByProject,
            threadId: threadByProject.threadId || threadByProject.id,
          });
        } catch (error) {}
      }
    },
    setAllMessagesFetched: (state, action) => {
      state.allMessagesFetched = true;
    },
    setAllMessagesRead: (state, action) => {
      const {
        projectId,
        originalThreadId,
        childThread,
        messagesReadCount,
        thread,
      } = action;
      const threadByProject = state.threadsByProject[projectId];

      const privateThread =
        (thread?.threadId || thread?.id) &&
        state.threads[thread.threadId || thread.id];

      if (!threadByProject && !privateThread) {
        return;
      }
      if (childThread) {
        threadByProject.unreadMessagesInChildProjects -= messagesReadCount || 0;
        threadByProject.children.find(
          (child) => child.id === originalThreadId
        ).unreadMessagesCount = 0;
      } else {
        if (threadByProject) {
          threadByProject.unreadMessagesCount = 0;
        }
      }

      if (privateThread) {
        privateThread.unreadMessagesCount = 0;
      }

      const parentProjectId = threadByProject?.parent;
      if (parentProjectId) {
        try {
          chatSlice.caseReducers.setAllMessagesRead(state, {
            projectId: parentProjectId,
            originalThreadId:
              originalThreadId ||
              threadByProject.threadId ||
              threadByProject.id,
            childThread: threadByProject,
            messagesReadCount,
            thread: threadByProject,
          });
        } catch {}
      }
    },
    setChatDialogOpen: (state, action) => {
      state.isChatDialogOpen = action.payload;
      if (!state.isChatDialogOpen) {
        state.isChatMinimized = false;
      }
      if (
        state.isChatDialogOpen &&
        state.messages &&
        state.messages.length > 0 &&
        state.thread
      ) {
        state.lastReadMessage = state.messages[0];
        chatSlice.caseReducers.setAllMessagesRead(state, {
          projectId: state.thread.projectId,
          messagesReadCount: state.unreadChatMessagesCount,
          thread: state.thread,
        });
      }
      // Clear notification when opening chat dialog
      if (state.isChatDialogOpen) {
        state.notifyMessage = null;
      }
    },
    setChatMinimized: (state, action) => {
      state.isChatMinimized = action.payload;
    },
    setLastEvaluatedKey: (state, action) => {
      const lastEvaluatedKey = action.payload;
      state.lastEvaluatedKey = lastEvaluatedKey;
    },
    setNewVideoChatSessionStarted: (state, action) => {
      state.newVideoChatSessionStarted = action.payload;
    },
    setNotifyMessage: (state, action) => {
      state.notifyMessage = action.payload;
    },
    setOpenedAttachment: (state, action) => {
      const attachment = action.payload;
      state.openedAttachment = attachment;
    },
    setProject: (state, action) => {
      const project = action.payload;

      if (project?.id !== state.project?.id) {
        state.project = project;
      }
    },
    setThread: (state, action) => {
      const thread = action.payload;
      if (thread?.id !== state.thread?.id) {
        // Snapshot the outgoing thread so returning to it can render instantly
        // (stale-while-revalidate); the refresh dispatched on open reconciles it.
        const outgoingId = state.thread?.id;
        if (outgoingId) {
          state.messagesCache[outgoingId] = {
            messages: state.messages,
            lastEvaluatedKey: state.lastEvaluatedKey,
            allMessagesFetched: state.allMessagesFetched,
          };
          state.videoSessionsCache[outgoingId] = {
            videoSessions: state.videoSessions,
            videoSessionsLastEvaluatedKey: state.videoSessionsLastEvaluatedKey,
          };
          state.messagesCacheOrder = [
            outgoingId,
            ...state.messagesCacheOrder.filter((id) => id !== outgoingId),
          ];
          while (state.messagesCacheOrder.length > MESSAGE_CACHE_LIMIT) {
            const evicted = state.messagesCacheOrder.pop();
            delete state.messagesCache[evicted];
            delete state.videoSessionsCache[evicted];
          }
        }

        state.thread = thread;

        const cached = thread?.id ? state.messagesCache[thread.id] : null;
        if (cached) {
          // Warm start: show cached messages immediately (no spinner). The initial
          // fetch on open runs as a refresh and merges anything that arrived since.
          state.messages = cached.messages;
          state.lastEvaluatedKey = cached.lastEvaluatedKey;
          state.allMessagesFetched = cached.allMessagesFetched;
          state.loading = false;
        } else {
          state.messages = [];
          state.lastEvaluatedKey = null;
          state.allMessagesFetched = false;
          state.loading = true;
        }
        state.lastReadMessage = null;
        state.unreadChatMessagesCount = thread?.unreadMessagesCount || 0;
        state.videoChatSession = null;
        // Restore cached video-call history for an instant warm open; the initial
        // fetch on open revalidates it (merging any sessions started since).
        const cachedVideoSessions = thread?.id
          ? state.videoSessionsCache[thread.id]
          : null;
        if (cachedVideoSessions) {
          state.videoSessions = cachedVideoSessions.videoSessions;
          state.videoSessionsLastEvaluatedKey =
            cachedVideoSessions.videoSessionsLastEvaluatedKey;
          state.videoSessionsLoading = false;
        } else {
          state.videoSessions = [];
          state.videoSessionsLastEvaluatedKey = null;
          state.videoSessionsLoading = true;
        }
        state.subThreads = [];
        if (thread?.userIds) {
          const filteredUsers = {};
          for (const userId of thread.userIds) {
            if (state.allUsers[userId]) {
              filteredUsers[userId] = state.allUsers[userId];
            }
          }
          state.users = filteredUsers;
        } else {
          state.users = state.projectUsers;
        }
        // Clear notification when opening a thread
        state.notifyMessage = null;
      }
    },
    setVideoChatOpen: (state, action) => {
      state.videoChatOpen = action.payload;
      if (!state.videoChatOpen && state.messages.length > 0 && state.thread) {
        state.lastReadMessage = state.messages[0];
        // The thread is what setAllMessagesRead resolves state.threads by, and
        // that is the copy every unread badge counts. Leaving it out cleared
        // threadsByProject alone, so ending a call marked the thread read
        // everywhere but the badge — and the message fetch deliberately skips
        // marking read while a call is open, so this is the only pass at it.
        chatSlice.caseReducers.setAllMessagesRead(state, {
          projectId: state.thread.projectId,
          messagesReadCount: state.unreadChatMessagesCount,
          thread: state.thread,
        });
      }
    },
    setVideoChatSession: (state, action) => {
      state.videoChatSession = action.payload;
    },
    setChatWebsocket: (state, action) => {
      state.chatWebsocket = action.payload;
    },
    addSubThread: (state, action) => {
      const newThread = action.payload;
      state.subThreads = [...state.subThreads, newThread];
      state.threads[newThread.threadId || newThread.id] = newThread;
    },
    // Authoritative replace of one thread's meetings (the dialog's full fetch).
    setThreadMeetings: (state, action) => {
      const { threadId, meetings } = action.payload;
      if (!threadId) return;
      state.meetingsByThread[threadId] = Array.isArray(meetings)
        ? meetings
        : [];
    },
    // Merge meetings into their threads by id (the app-load upcoming fetch and
    // MEETING websocket events). Never drops meetings a fuller fetch already
    // loaded — a cancel arrives as an upsert carrying cancelledAt, which the
    // consumers filter out.
    upsertMeetings: (state, action) => {
      const meetings = action.payload || [];
      for (const meeting of meetings) {
        const threadId = meeting?.threadId;
        if (!threadId || !meeting.id) continue;
        const list = state.meetingsByThread[threadId] || [];
        const idx = list.findIndex((m) => m.id === meeting.id);
        if (idx !== -1) {
          list[idx] = meeting;
        } else {
          list.push(meeting);
        }
        state.meetingsByThread[threadId] = list;
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(handleWebsocketMessageThunk.fulfilled, (state, action) => {
        const { message, threadWasFetched } = action.payload;
        const messageSender = message?.userId;

        const { user } = action.meta.arg;
        if (message) {
          const messageType = message.type.split('#')[0];
          if (messageType === 'MESSAGE' && messageSender === user?.id) {
            // Own message confirmed by WebSocket — update the optimistic entry
            const isCurrentThreadMessage =
              message.threadId === (state.thread?.threadId || state.thread?.id);
            if (isCurrentThreadMessage) {
              if (message.isAttachment && message.attachedTo) {
                // Attachment: link to parent (parent already has the real id
                // from a prior WS echo of the text message)
                chatSlice.caseReducers.addMessage(state, { payload: message });
              }
            }
          } else if (messageType === 'MESSAGE' && messageSender !== user?.id) {
            const isCurrentThreadMessage =
              message.threadId === (state.thread?.threadId || state.thread?.id);
            if (isCurrentThreadMessage) {
              chatSlice.caseReducers.addMessage(state, { payload: message });
            }
            if (
              !message.isAttachment &&
              (!isCurrentThreadMessage || !state.isChatDialogOpen)
            ) {
              // Only increment unread count if we didn't just fetch the thread
              // (fetched threads already have the correct count from backend)
              if (!threadWasFetched) {
                chatSlice.caseReducers.addUnreadMessageToThread(state, {
                  projectId: message.projectId,
                  threadId: message.threadId,
                });
              }
              state.notifyMessage = message;
            }
          } else if (
            messageType === 'LAST_READ_MESSAGE' &&
            messageSender !== user?.id
          ) {
            const { threadId, userId, updatedAt } = message;
            if (threadId === state.thread?.id) {
              let previousLastReadTime = null;
              let messagesToUpdate = [];
              const lastReadMessageItem = state.lastReadMessages.find(
                (item) => item.userId === userId
              );
              if (lastReadMessageItem) {
                previousLastReadTime = lastReadMessageItem.updatedAt;
                messagesToUpdate = state.messages.filter(
                  (m) =>
                    (!m.createdAt ||
                      (m.createdAt > previousLastReadTime &&
                        m.createdAt <= updatedAt)) &&
                    m.userId !== userId
                );
                const index =
                  state.lastReadMessages.indexOf(lastReadMessageItem);
                state.lastReadMessages[index] = {
                  ...lastReadMessageItem,
                  ...message,
                };
              } else {
                state.lastReadMessages.push(message);
                messagesToUpdate = state.messages.filter(
                  (m) => m.createdAt <= updatedAt && m.userId !== userId
                );
              }
              for (const messageToUpdate of messagesToUpdate) {
                if (!messageToUpdate.readBy) {
                  messageToUpdate.readBy = [];
                }
                const readByEntry = messageToUpdate.readBy.find(
                  (u) => u.userId === userId
                );
                if (readByEntry) {
                  readByEntry.readAt = updatedAt;
                } else {
                  messageToUpdate.readBy.push({
                    userId: userId,
                    firstName: lastReadMessageItem?.firstName || '',
                    lastName: lastReadMessageItem?.lastName || '',
                    readAt: updatedAt,
                  });
                }
              }
            }
          } else if (messageType === 'VIDEOCHAT_SESSION') {
            const { threadId, isActive, directRecipient } = message;

            if (isActive && directRecipient) {
              state.notifyMessage = message;
            }
            // Track project-wide calls regardless of which thread is open, so
            // the dashboard and projects list can show them. Every joiner
            // re-broadcasts the same active session, so this write is
            // idempotent. Private 1:1 calls are excluded.
            if (threadId && !message.isPrivate && !directRecipient) {
              if (isActive) {
                state.activeCallsByThreadId[threadId] = {
                  id: message.id,
                  createdAt: message.createdAt,
                  startedByUserId: message.userId,
                };
              } else {
                delete state.activeCallsByThreadId[threadId];
              }
            }
            if (threadId === (state.thread?.threadId || state.thread?.id)) {
              if (
                isActive &&
                !state.videoChatSession?.isActive &&
                messageSender !== user?.id
              ) {
                state.newVideoChatSessionStarted = true;
              }
              state.videoChatSession = message;

              // Upsert into videoSessions array for display in message list
              if (message.id) {
                const idx = state.videoSessions.findIndex(
                  (s) => s.id === message.id
                );
                if (idx !== -1) {
                  state.videoSessions[idx] = {
                    ...state.videoSessions[idx],
                    ...message,
                    // Preserve recordings accumulated from prior WS events;
                    // treat an empty array from WS the same as absent
                    recordings:
                      message.recordings?.length > 0
                        ? message.recordings
                        : state.videoSessions[idx].recordings,
                  };
                } else {
                  state.videoSessions = [message, ...state.videoSessions];
                }
              }
            }
          } else if (messageType === 'VIDEOCHAT_RECORDING') {
            const {
              threadId,
              sessionId,
              id,
              isActive,
              startedAt,
              endedAt,
              recordingOutputKey,
              conversionFailedAt,
            } = message;
            const currentThreadId = state.thread?.threadId || state.thread?.id;
            if (threadId === currentThreadId || !threadId) {
              const sessionIdx = sessionId
                ? state.videoSessions.findIndex((s) => s.id === sessionId)
                : state.videoSessions.findIndex((s) => s.isActive);
              if (sessionIdx !== -1 && id) {
                const recordings =
                  state.videoSessions[sessionIdx].recordings || [];
                const recIdx = recordings.findIndex((r) => r.id === id);
                const recordingUpdate = {
                  id,
                  isActive,
                  startedAt,
                  endedAt,
                  ...(recordingOutputKey !== undefined && {
                    recordingOutputKey,
                  }),
                  ...(conversionFailedAt !== undefined && {
                    conversionFailedAt,
                  }),
                };
                if (recIdx !== -1) {
                  state.videoSessions[sessionIdx].recordings = recordings.map(
                    (r, i) => (i === recIdx ? { ...r, ...recordingUpdate } : r)
                  );
                } else {
                  state.videoSessions[sessionIdx].recordings = [
                    ...recordings,
                    recordingUpdate,
                  ];
                }
              }
            }
          } else if (messageType === 'VIDEOCHAT_ATTENDEE') {
            const { threadId, sessionId, userId: attendeeUserId } = message;
            const currentThreadId = state.thread?.threadId || state.thread?.id;
            if (threadId === currentThreadId || !threadId) {
              const foundSessionIndex = sessionId
                ? state.videoSessions.findIndex((s) => s.id === sessionId)
                : state.videoSessions.findIndex((s) => s.isActive);
              if (foundSessionIndex !== -1 && attendeeUserId) {
                const existing =
                  state.videoSessions[foundSessionIndex].attendeeUserIds || [];
                if (!existing.includes(attendeeUserId)) {
                  state.videoSessions[foundSessionIndex].attendeeUserIds = [
                    ...existing,
                    attendeeUserId,
                  ];
                }
              }
            }
          } else if (messageType === 'MEETING') {
            // A meeting was created, rescheduled, or cancelled on a thread the
            // user is subscribed to. Patch it into the per-thread store so open
            // dialogs update their banners/markers live and the scheduler sees
            // it. Cancelled meetings arrive with cancelledAt and are filtered
            // out by the consumers.
            chatSlice.caseReducers.upsertMeetings(state, {
              payload: [message],
            });
          }
        }
      })
      .addCase(loadThreadOnDemand.fulfilled, (state, action) => {
        const thread = action.payload;
        const threadKey = thread.threadId || thread.id;
        const existingByProject = state.threadsByProject[thread.projectId];
        const existingThread = state.threads[threadKey];
        const merged = {
          ...thread,
          hasMessages:
            thread.hasMessages ||
            existingByProject?.hasMessages ||
            existingThread?.hasMessages ||
            false,
          lastMessageAt: maxTimestamp(
            thread.lastMessageAt,
            maxTimestamp(
              existingByProject?.lastMessageAt,
              existingThread?.lastMessageAt
            )
          ),
        };
        const threadsByProject = { ...state.threadsByProject };
        threadsByProject[thread.projectId] = merged;
        state.threads[threadKey] = merged;
        state.threadsByProject = threadsByProject;
      })
      .addCase(sendChatMessageThunk.pending, (state, action) => {
        const { message, attachment } = action.meta.arg;
        if (!message.attachedTo) {
          if (attachment) {
            const objectUrl = URL.createObjectURL(attachment);
            message.imagePreview = objectUrl;
            message.attachmentPreviewName = attachment.name;
          }
          const messageExists =
            message.id && state.messages.some((m) => m.id === message.id);
          if (!messageExists) {
            state.messages = [message, ...state.messages];
          }
        }
      })
      .addCase(fetchChatMessagesThunk.fulfilled, (state, action) => {
        const { limit } = action.meta.arg;
        const { messages, refresh } = action.payload;
        const previousMessages = state.messages || [];

        if (refresh) {
          // Revalidate a freshly-opened (possibly cached) thread: merge the newest
          // page into whatever is already shown, de-duplicating by id and leaving the
          // backward-pagination cursor untouched so scroll paging keeps working.
          state.loading = false;
          if (messages.length === 0) {
            if (previousMessages.length === 0) {
              state.allMessagesFetched = true;
            }
            return;
          }
          const incomingMessages = messages
            .filter((message) => !message.isAttachment)
            .map((message) => setReadBy(message, state));
          const incomingAttachments = messages.filter(
            (message) => message.isAttachment && message.attachedTo
          );
          const existingIds = new Set(previousMessages.map((m) => m.id));
          const overlaps = incomingMessages.some((m) => existingIds.has(m.id));
          const wasEmpty = previousMessages.length === 0;
          // A full page with no overlap means more than a page arrived while away —
          // rebuild from the fresh page (and reset the cursor) to avoid a gap.
          const rebuild = wasEmpty || (!overlaps && messages.length >= limit);

          let merged;
          let attachmentsToLink;
          if (rebuild) {
            merged = incomingMessages;
            attachmentsToLink = incomingAttachments;
            const oldest = messages.at(-1);
            state.lastEvaluatedKey = {
              id: oldest.id,
              target: oldest.target,
              type: oldest.type,
            };
            state.allMessagesFetched = messages.length < limit;
          } else {
            // Keep the cached copies of overlapping messages — they already carry
            // resolved attachment image URLs — and only prepend genuinely new ones.
            // Replacing them with the fresh, URL-less copies would drop those URLs
            // and force every image to be re-fetched on each revisit.
            const incomingNew = incomingMessages.filter(
              (m) => !existingIds.has(m.id)
            );
            merged = [...incomingNew, ...previousMessages];
            const newIds = new Set(incomingNew.map((m) => m.id));
            attachmentsToLink = incomingAttachments.filter((a) =>
              newIds.has(a.attachedTo)
            );
          }
          for (const attachment of attachmentsToLink) {
            const attachedTo = merged.find(
              (m) => m.id === attachment.attachedTo
            );
            if (attachedTo) {
              attachedTo.attachments = [attachment];
              delete attachedTo.imagePreview;
              delete attachedTo.attachmentPreviewName;
            }
          }
          state.messages = merged;
          state.needImageUrls = true;

          // Opening a thread with the dialog focused marks it read. Before caching
          // every open was a cold open and hit this, so mark read on warm returns too
          // (not just wasEmpty) or the unread badge would linger on revisits.
          if (state.isChatDialogOpen && !state.videoChatOpen) {
            state.lastReadMessage = messages[0];
            chatSlice.caseReducers.setAllMessagesRead(state, {
              projectId: state.thread.projectId,
              messagesReadCount: state.unreadChatMessagesCount,
              thread: state.thread,
            });
          }
          return;
        }

        chatSlice.caseReducers.addMessagesToStart(state, action);
        state.loading = false;

        if (messages.length < limit) {
          state.allMessagesFetched = true;
        }
        if (messages.length > 0) {
          const lastMessage = messages.at(-1);
          const key = {
            id: lastMessage.id,
            target: lastMessage.target,
            type: lastMessage.type,
          };
          state.lastEvaluatedKey = key;
          state.needImageUrls = true;
          if (
            previousMessages.length === 0 &&
            state.isChatDialogOpen &&
            !state.videoChatOpen
          ) {
            state.lastReadMessage = messages[0];
            chatSlice.caseReducers.setAllMessagesRead(state, {
              projectId: state.thread.projectId,
              messagesReadCount: state.unreadChatMessagesCount,
              thread: state.thread,
            });
          }
        }
      })
      .addCase(getImageUrlsThunk.fulfilled, (state, action) => {
        const { messagesWithAttachments } = action.payload;
        const messages = state.messages;
        for (const messageWithAttachments of messagesWithAttachments) {
          const message = messages.find(
            (m) => m.id === messageWithAttachments.id
          );
          if (message) {
            message.attachments = messageWithAttachments.attachments;
          }
        }
        state.messages = messages;
        state.needImageUrls = false;
      })
      .addCase(getProjectThreadThunk.fulfilled, (state, action) => {
        const thread = action.payload;
        const { projectId } = action.meta.arg;
        const threadKey = thread.threadId || thread.id;
        const existingByProject = state.threadsByProject[projectId];
        const existingThread = state.threads[threadKey];
        const merged = {
          ...thread,
          hasMessages:
            thread.hasMessages ||
            existingByProject?.hasMessages ||
            existingThread?.hasMessages ||
            false,
          lastMessageAt: maxTimestamp(
            thread.lastMessageAt,
            maxTimestamp(
              existingByProject?.lastMessageAt,
              existingThread?.lastMessageAt
            )
          ),
        };
        const threadsByProject = { ...state.threadsByProject };
        threadsByProject[projectId] = merged;
        state.threadsByProject = threadsByProject;
        state.threads[threadKey] = merged;
        if (projectId === state.project?.id) {
          chatSlice.caseReducers.setThread(state, { payload: merged });
        }
      })
      .addCase(getProjectThreadsThunk.fulfilled, (state, action) => {
        const { threadsByProject, threads } = action.payload;
        state.threadsByProject = threadsByProject;
        state.threads = { ...state.threads, ...threads };
        // Seed ongoing calls, so an indicator survives a reload instead of
        // waiting for the next start/end event.
        for (const thread of Object.values(threads)) {
          const threadKey = thread.threadId || thread.id;
          if (!threadKey) continue;
          if (thread.activeVideochatSession) {
            state.activeCallsByThreadId[threadKey] = {
              id: thread.activeVideochatSession.id,
              createdAt: thread.activeVideochatSession.createdAt,
              startedByUserId: thread.activeVideochatSession.startedByUserId,
            };
          } else {
            delete state.activeCallsByThreadId[threadKey];
          }
        }
      })
      .addCase(getPrivateThreadThunk.fulfilled, (state, action) => {
        const { thread, setAsActive } = action.payload;
        if (thread?.id) {
          const threadKey = thread.threadId || thread.id;
          const existing = state.threads[threadKey];
          const merged = {
            ...thread,
            hasMessages: thread.hasMessages || existing?.hasMessages || false,
            lastMessageAt: maxTimestamp(
              thread.lastMessageAt,
              existing?.lastMessageAt
            ),
          };
          // Only set as active thread if explicitly requested
          if (setAsActive) {
            chatSlice.caseReducers.setThread(state, { payload: merged });
            chatSlice.caseReducers.setProject(state, { payload: null });
          }
          state.threads[threadKey] = merged;
        }
      })
      .addCase(getPrivateThreadsThunk.fulfilled, (state, action) => {
        const threads = action.payload;
        state.threads = { ...state.threads, ...threads };
      })
      .addCase(getLastReadMessagesForThreadThunk.fulfilled, (state, action) => {
        const lastReadMessages = action.payload;
        state.lastReadMessages = lastReadMessages;
        const updatedMessages = state.messages.map((message) =>
          setReadBy(message, state)
        );
        state.messages = updatedMessages;
      })
      .addCase(getMessageReadTimeThunk.fulfilled, (state, action) => {
        const { userId, createdAt } = action.payload;
        const message = state.messages.find(
          (m) => m.id === action.meta.arg.message.id
        );
        if (message) {
          const readByEntry = message.readBy.find((u) => u.userId === userId);
          if (readByEntry) {
            readByEntry.readAt = createdAt;
          }
        }
      })
      .addCase('chat/getVideoChatSession/fulfilled', (state, action) => {
        state.videoChatSession = action.payload.session;
      })
      .addCase('chat/loadUsers/fulfilled', (state, action) => {
        const { addToCurrentUsers } = action.meta.arg;
        if (addToCurrentUsers) {
          state.users = { ...state.users, ...action.payload };
        }
        state.allUsers = { ...state.allUsers, ...action.payload };
      })
      .addCase('chat/getProjectUsers/fulfilled', (state, action) => {
        const { addToCurrentUsers } = action.meta.arg;
        if (addToCurrentUsers) {
          state.users = action.payload;
        }
        state.projectUsers = action.payload;
        state.allUsers = { ...state.allUsers, ...action.payload };
      })
      .addCase('chat/updateLastReadMessage/fulfilled', (state, action) => {
        state.unreadChatMessagesCount = 0;
      })
      .addCase('chat/getProjectSubthreads/fulfilled', (state, action) => {
        const subThreads = action.payload;
        state.subThreads = subThreads;
        for (const subThread of subThreads) {
          const threadKey = subThread.threadId || subThread.id;
          const existing = state.threads[threadKey];
          state.threads[threadKey] = {
            ...subThread,
            hasMessages:
              subThread.hasMessages || existing?.hasMessages || false,
            lastMessageAt: maxTimestamp(
              subThread.lastMessageAt,
              existing?.lastMessageAt
            ),
          };
        }
      })
      .addCase('chat/addUsersToThread/fulfilled', (state, action) => {
        const updatedThread = action.payload;
        const threadKey = updatedThread.threadId || updatedThread.id;
        const existing = state.threads[threadKey];
        const merged = {
          ...updatedThread,
          hasMessages:
            updatedThread.hasMessages || existing?.hasMessages || false,
          lastMessageAt: maxTimestamp(
            updatedThread.lastMessageAt,
            existing?.lastMessageAt
          ),
        };
        state.threads[threadKey] = merged;
        if (state.thread?.id === updatedThread.id) {
          state.thread = merged;
          const filteredUsers = {};
          for (const userId of updatedThread.userIds) {
            if (state.allUsers[userId]) {
              filteredUsers[userId] = state.allUsers[userId];
            }
          }
          state.users = filteredUsers;
        }
      })
      .addCase('chat/getThread/fulfilled', (state, action) => {
        const thread = action.payload;
        const threadKey = thread.threadId || thread.id;
        const existing = state.threads[threadKey];
        const merged = {
          ...thread,
          hasMessages: thread.hasMessages || existing?.hasMessages || false,
          lastMessageAt: maxTimestamp(
            thread.lastMessageAt,
            existing?.lastMessageAt
          ),
        };
        state.threads[threadKey] = merged;
        if (state.thread?.threadId === thread.id) {
          state.thread = merged;
        }
      })
      .addCase('chat/getSubthread/fulfilled', (state, action) => {
        const thread = action.payload;
        const threadKey = thread.threadId || thread.id;
        const existing = state.threads[threadKey];
        const merged = {
          ...thread,
          hasMessages: thread.hasMessages || existing?.hasMessages || false,
          lastMessageAt: maxTimestamp(
            thread.lastMessageAt,
            existing?.lastMessageAt
          ),
        };
        state.threads[threadKey] = merged;
        if (state.thread?.threadId === thread.id) {
          state.thread = merged;
        }
      })
      .addCase(getThreadVideoSessionsThunk.fulfilled, (state, action) => {
        const { sessions, lastEvaluatedKey } = action.payload;
        // A fetch with no cursor arg is the initial open fetch — and doubles as the
        // stale-while-revalidate pass for a warm-restored thread. Merge the newest
        // page in, de-duping by id; only (re)set the cursor when starting from empty
        // so a restored cache doesn't re-cascade pages it already holds. Cascade
        // pages (dispatched with a cursor arg) keep appending and advancing it.
        const isInitialFetch = !action.meta.arg?.lastEvaluatedKey;
        if (isInitialFetch) {
          if (state.videoSessions.length === 0) {
            state.videoSessions = sessions;
            state.videoSessionsLastEvaluatedKey = lastEvaluatedKey || null;
          } else {
            const existingIds = new Set(state.videoSessions.map((s) => s.id));
            state.videoSessions = [
              ...state.videoSessions,
              ...sessions.filter((s) => !existingIds.has(s.id)),
            ];
          }
          state.videoSessionsLoading = false;
        } else {
          state.videoSessions = [...state.videoSessions, ...sessions];
          state.videoSessionsLastEvaluatedKey = lastEvaluatedKey || null;
        }
      })
      .addCase(getThreadVideoSessionsThunk.rejected, (state) => {
        state.videoSessionsLoading = false;
      });
  },
});

// ACTIONS
export const {
  addMessagesToStart,
  addMessage,
  setAllMessagesFetched,
  setLastEvaluatedKey,
  setProject,
  setThread,
  setOpenedAttachment,
  setChatDialogOpen,
  setChatMinimized,
  setChatWebsocket,
  setVideoChatSession,
  setNewVideoChatSessionStarted,
  setVideoChatOpen,
  setAllMessagesRead,
  setNotifyMessage,
  addSubThread,
  setThreadMeetings,
  upsertMeetings,
} = chatSlice.actions;

// SELECTORS
export const getChatControlData = (state) => {
  return state.chatSlice;
};

const EMPTY_MEETINGS = [];

export const selectMeetingsByThread = (state) =>
  state.chatSlice.meetingsByThread;

// One thread's meetings (stable reference until they change). Consumers do
// their own cancelled/time filtering.
export const selectThreadMeetings = (threadId) => (state) =>
  (threadId && state.chatSlice.meetingsByThread[threadId]) || EMPTY_MEETINGS;

// Every non-cancelled meeting across all known threads, soonest first. Time
// filtering (upcoming vs past) is left to consumers, which re-check on their
// own clock tick — so this stays memoized on the store, not the clock.
export const selectAllMeetings = createSelector(
  [selectMeetingsByThread],
  (meetingsByThread) =>
    Object.values(meetingsByThread)
      .flat()
      .filter((meeting) => !meeting.cancelledAt)
      .sort((a, b) => (a.startsAt < b.startsAt ? -1 : 1))
);
